<template>
  <Overlay unique-key="FitmentModal" modal-width="WIDE" title="Select Your Vehicle" @closed="close">
    <template v-if="isInExperiment" #topBar>
      <div class="bg-black w-full flex justify-between py-2 pl-3 lg:py-3 lg:px-6">
        <img src="/images/rt-logo.svg" alt="" class="h-6 w-auto" width="198" height="32" />
      </div>
    </template>

    <template v-if="isInExperiment" #header="{ title }">
      <div v-if="title" class="lg:flex items-center">
        <div class="title m-0 leading-6">{{ title }}</div>
        <div v-if="$sitewideConfig.config.guaranteedFitEnabled" class="flex items-center mt-2 lg:mt-0">
          <span class="lg:ml-2 normal-case font-normal text-base"
            >{{ $device.isDesktop ? ' — ' : '' }}Shop parts Guaranteed to Fit</span
          >
          <Tooltip position="bottom">
            <InfoIcon class="m-1 w-3 h-4 text-action fill-current align-middle" aria-label="Guaranteed Fit Details" />
            <template #text>
              <div class="text-base">
                We guarantee fit on thousands of products or your money back.
                <UniversalLink link="/guaranteed-fit/" :open-new-window="true">Learn more</UniversalLink>
              </div>
            </template>
          </Tooltip>
        </div>
      </div>
    </template>

    <div data-testid="fitmentModal">
      <client-only>
        <FitmentSelect
          v-for="select in displayStore.modalRequiredSelectTypes"
          :key="select.type"
          data-testid="requiredSelect"
          :select="select"
          :current-value="select.currentValue"
          class="mb-3"
          @changed="fitmentDisplay.selectChanged"
        />
      </client-only>
    </div>

    <template #footer>
      <div v-if="displayStore.modal.isFitsMode" class="text-center">
        <BaseButton :is-disabled="!fitmentDisplay.allDoneSelecting.value" size="LARGE" @click="viewFits"
          >View Items That Fit</BaseButton
        >
      </div>

      <template v-else>
        <div class="flex pb-6">
          <div class="flex-1 pr-3">
            <BaseButton color="GRAY" class="w-full" data-testid="fitmentClear" @click="clear">Clear</BaseButton>
          </div>

          <div class="flex-1 pl-3">
            <BaseButton
              :is-disabled="!fitmentDisplay.allDoneSelecting.value"
              class="w-full"
              data-testid="fitmentSave"
              @click="save"
              >Save</BaseButton
            >
          </div>
        </div>

        <BaseButton
          v-if="isIntegrationEnabled('protex') && hasProtexFitment"
          to="PROTEX"
          color="PRIMARY"
          has-custom-text-color
          class="w-full text-black"
          @click="launchBuilder"
          >Launch RealTruck Builder</BaseButton
        >
      </template>
    </template>
  </Overlay>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import InfoIcon from '@/assets/info.svg?component'

const displayStore = useFitmentDisplayStore()
const fitmentDisplay = useFitmentDisplay()
const fitmentStore = useFitmentStore()
const fitmentDisplayStore = useFitmentDisplayStore()
const routeBuilder = useRouteBuilder()
const route = useRoute()
const { $device, $overlay, $uiEvents, $sitewideConfig } = useNuxtApp()
const amplitude = useAmplitude()
const { isIntegrationEnabled } = useUtils()
const protex = useProtex()
const hasProtexFitment = ref(false)

const isInExperiment = ref(false)
// Experiment: On the Home Page auto load the fitment modal if we don't have full fitment
if (route.name === 'home' && !displayStore.hasFullFitment) {
  amplitude.onReady({
    experimentId: 'forced-fitment',
    callback: (variant) => {
      if (variant.value !== 'treatment') return

      isInExperiment.value = true
      displayStore.showFitmentModal()
    },
  })
}

onMounted(async () => {
  fitmentDisplay.layoutPosition.value = 'modal'

  if (fitmentDisplay.allDoneSelecting.value) hasProtexFitment.value = await getProtexFitment()
})

watch(
  () => fitmentDisplay.allDoneSelecting.value,
  async (value) => {
    if (value) hasProtexFitment.value = await getProtexFitment()
    else hasProtexFitment.value = false
  }
)

function save() {
  fitmentDisplay.saveFitment()
  // Mobile redirects no matter what with ?mod but desktop needs some help closing the modal
  if ($device.value.isDesktop) $overlay.close()
}

async function getProtexFitment() {
  return await protex.hasFitment({
    make: getFitmentValue('make'),
    model: getFitmentValue('model'),
    year: getFitmentValue('year'),
    bed: getFitmentValue('bed'),
    body: getFitmentValue('body'),
  })
}

function launchBuilder() {
  save()
  $uiEvents.$emit('FitmentBuilderButtonClicked', {
    linkText: 'Launch RealTruck Builder',
    clickUrl: '/builder/',
    clickType: 'RTB Launch - Fitment Modal',
    currentUrl: route.path,
  })
}

function getFitmentValue(key: FitmentTextKey) {
  // Unselected fitment values are '-1'
  if (fitmentDisplayStore.getSelect(key)?.currentValue === '-1') return
  return fitmentDisplayStore.getSelect(key)?.currentValue
}

function viewFits() {
  fitmentDisplay.saveFitment()
  navigateTo({
    name: 'mmy',
    params: {
      makeSlug: fitmentStore.$state.makeSlug,
      modelSlug: fitmentStore.$state.modelSlug,
      year: fitmentStore.$state.year,
    },
  })
}

function close() {
  displayStore.resetFitmentModal()

  if (isIntegrationEnabled('amplitude')) {
    // We only show the banner when auto triggered as part of the test
    // so reset it back to false.
    isInExperiment.value = false
  }

  // If we are in the isSameDayShippingMode mode and we don't have full fitment
  if (displayStore.modal.isSameDayShippingMode && !displayStore.hasFullFitment) {
    const query = { ...route.query }

    if (query.sameDayShipping) {
      // This removes the sameDayShipping stuff for the catalog page.
      delete query.sameDayShipping

      // Push to new route as Replace
      navigateTo({ ...route, query }, { replace: true })
      return
    }
  }
}

function clear() {
  displayStore.clearFitment()

  if (route.name === 'mmy') {
    navigateTo('/')
    return
  }

  const newRoute: RouteLocationRaw = {
    name: route.name || '',
    params: Object.assign({}, route.params),
    query: Object.assign({}, route.query),
  }

  if (newRoute.name === 'search') newRoute.query = { ...newRoute.query, z: Date.now().toString() }

  if (newRoute.name && ['product-mmy', 'sku'].includes(newRoute.name.toString())) {
    newRoute.query = { z: Date.now().toString() }
  }

  if (newRoute.query?.mod) delete newRoute.query.mod
  if (newRoute.query?.sameDayShipping) delete newRoute.query.sameDayShipping
  navigateTo(routeBuilder.formatRoute(newRoute))
}
</script>
